import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import AteliaLogo from '../components/AteliaLogo';
import { ScanLine, Sparkles, Leaf, ArrowRight, Search, Droplets, Zap, AlertCircle, Check, X, Loader2, Shield, Award, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InsightsScreen = () => {
  const { scanHistory, currentProduct, setCurrentScreen, setActiveTab } = useApp();
  const [activeSection, setActiveSection] = useState('overview'); // 'overview', 'fabric-lookup', or 'cert-filter'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [fabricList, setFabricList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  
  // Certification filter state
  const [certifications, setCertifications] = useState([]);
  const [selectedCert, setSelectedCert] = useState(null);
  const [certBrands, setCertBrands] = useState([]);
  const [loadingCerts, setLoadingCerts] = useState(false);
  const [loadingCertBrands, setLoadingCertBrands] = useState(false);

  const lastScanned = scanHistory[0] || currentProduct;

  const handleStartScan = () => {
    setActiveTab('scan');
    setCurrentScreen('scan');
  };

  // Load fabric list on mount
  useEffect(() => {
    const loadFabrics = async () => {
      setLoadingList(true);
      try {
        const response = await fetch(`${API}/fabrics/list`);
        if (response.ok) {
          const data = await response.json();
          setFabricList(data.fabrics || []);
        }
      } catch (err) {
        console.error('Error loading fabrics:', err);
      } finally {
        setLoadingList(false);
      }
    };
    loadFabrics();
  }, []);

  // Load certifications list on mount
  useEffect(() => {
    const loadCertifications = async () => {
      setLoadingCerts(true);
      try {
        const response = await fetch(`${API}/certifications/list-all`);
        if (response.ok) {
          const data = await response.json();
          setCertifications(data.certifications || []);
        }
      } catch (err) {
        console.error('Error loading certifications:', err);
      } finally {
        setLoadingCerts(false);
      }
    };
    loadCertifications();
  }, []);

  // Load brands by certification when selected
  const loadBrandsByCertification = async (cert) => {
    setSelectedCert(cert);
    setLoadingCertBrands(true);
    setCertBrands([]);
    
    try {
      const response = await fetch(`${API}/brands/by-certification/${encodeURIComponent(cert)}`);
      if (response.ok) {
        const data = await response.json();
        setCertBrands(data.brands || []);
      }
    } catch (err) {
      console.error('Error loading brands by certification:', err);
    } finally {
      setLoadingCertBrands(false);
    }
  };

  const searchFabric = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    setSearchResult(null);
    
    try {
      const response = await fetch(`${API}/fabrics/search?query=${encodeURIComponent(searchQuery)}`);
      if (response.ok) {
        const data = await response.json();
        setSearchResult(data);
      }
    } catch (err) {
      console.error('Error searching fabric:', err);
    } finally {
      setSearching(false);
    }
  };

  const handleFabricClick = (fabricKey) => {
    setSearchQuery(fabricKey.replace(/_/g, ' '));
    setActiveSection('fabric-lookup');
    setTimeout(() => searchFabric(), 100);
  };

  // Get rating color
  const getRatingColor = (rating) => {
    if (rating >= 4) return '#1B7340';
    if (rating >= 3) return '#4A9F5B';
    if (rating >= 2) return '#996B3D';
    return '#B85C3B';
  };

  // Certification Filter Section
  const renderCertificationFilter = () => (
    <div className="px-6 py-4">
      {/* Header */}
      <div className="mb-4">
        <h2 className="font-serif text-lg font-light text-gray-900">Browse by Certification</h2>
        <p className="text-xs text-gray-400 mt-1">Find brands with verified sustainability certifications</p>
      </div>

      {/* Certification List */}
      {!selectedCert ? (
        <div className="space-y-2">
          {loadingCerts ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-gray-400" />
            </div>
          ) : (
            certifications.map((cert, i) => (
              <button
                key={i}
                onClick={() => loadBrandsByCertification(cert.name)}
                className="w-full premium-card rounded-xl p-4 luxury-border flex items-center justify-between
                  hover:shadow-md hover:border-[#000000]/20 transition-all"
                data-testid={`cert-filter-${cert.name}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#1B7340]/5">
                    <Award size={18} className="text-[#1B7340]" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">{cert.name}</p>
                    <p className="text-xs text-gray-400">{cert.brand_count} brands</p>
                  </div>
                </div>
                <ChevronRight size={16} className="text-gray-200" />
              </button>
            ))
          )}
        </div>
      ) : (
        <div>
          {/* Back button */}
          <button
            onClick={() => {
              setSelectedCert(null);
              setCertBrands([]);
            }}
            className="flex items-center gap-2 text-gray-900 text-sm mb-4 hover:underline"
          >
            <ChevronRight size={14} className="rotate-180" />
            <span>Back to all certifications</span>
          </button>

          {/* Selected Certification Header */}
          <div className="premium-card rounded-xl p-4 luxury-border mb-4 bg-[#1B7340]/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#1B7340]/10">
                <Shield size={20} className="text-[#1B7340]" />
              </div>
              <div>
                <h3 className="font-serif text-lg font-light text-gray-900">{selectedCert}</h3>
                <p className="text-xs text-gray-500">{certBrands.length} brands with this certification</p>
              </div>
            </div>
          </div>

          {/* Brands List */}
          <div className="space-y-2">
            {loadingCertBrands ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={24} className="animate-spin text-gray-400" />
              </div>
            ) : certBrands.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-400">No brands found with this certification</p>
              </div>
            ) : (
              certBrands.map((brand, i) => (
                <div
                  key={i}
                  className="premium-card rounded-xl p-4 luxury-border"
                  data-testid={`cert-brand-${brand.key}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900">{brand.name}</h4>
                    {brand.rating > 0 && (
                      <div 
                        className="px-2 py-1 rounded-lg text-xs font-medium text-gray-900"
                        style={{ backgroundColor: getRatingColor(brand.rating) }}
                      >
                        {brand.rating_label}
                      </div>
                    )}
                  </div>
                  
                  {/* Certifications */}
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {brand.certifications.map((cert, j) => (
                      <span
                        key={j}
                        className={`px-2 py-0.5 text-[10px] rounded-lg border ${
                          cert === selectedCert 
                            ? 'bg-[#1B7340]/10 text-[#1B7340] border-[#1B7340]/20' 
                            : 'bg-gray-50/[0.03] text-gray-500 border-[#000000]/20'
                        }`}
                      >
                        {cert}
                      </span>
                    ))}
                  </div>
                  
                  {/* Categories & Price Segment */}
                  <div className="flex items-center gap-3 text-[10px] text-gray-400">
                    {brand.categories?.length > 0 && (
                      <span>{brand.categories.slice(0, 2).join(', ')}</span>
                    )}
                    {brand.price_segment && brand.price_segment !== 'Unknown' && (
                      <>
                        <span>•</span>
                        <span>{brand.price_segment}</span>
                      </>
                    )}
                    {brand.headquarters && brand.headquarters !== 'Unknown' && (
                      <>
                        <span>•</span>
                        <span>{brand.headquarters}</span>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );

  // Fabric Lookup Section
  const renderFabricLookup = () => (
    <div className="px-6 py-4">
      {/* Search Bar */}
      <div className="relative mb-4">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && searchFabric()}
          placeholder="Search any fabric (cotton, lyocell, silk...)"
          className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-[#000000]/20 
            rounded-xl text-sm focus:outline-none focus:border-[#000000]/30 focus:ring-1 
            focus:ring-[#000000]/10 transition-all"
        />
        <button
          onClick={searchFabric}
          disabled={searching || !searchQuery.trim()}
          className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-white 
            text-gray-900 text-xs rounded-lg hover:bg-gray-50 transition-colors
            disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {searching ? <Loader2 size={14} className="animate-spin" /> : 'Search'}
        </button>
      </div>

      {/* Search Result */}
      {searchResult && (
        <div className="mb-6">
          {searchResult.found ? (
            <div className="premium-card rounded-2xl luxury-border overflow-hidden">
              {/* Header with Eco-Tier */}
              <div className="p-5 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-serif text-xl font-light text-gray-900 capitalize">
                      {searchResult.fabric_name.replace(/_/g, ' ')}
                    </h3>
                    <p className="text-xs text-gray-400 capitalize mt-1">
                      {searchResult.category?.replace(/_/g, ' ')}
                    </p>
                  </div>
                  <div 
                    className="px-3 py-1.5 rounded-full flex items-center gap-2"
                    style={{ backgroundColor: `${searchResult.eco_tier_color}15` }}
                  >
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-gray-900 text-xs font-medium"
                      style={{ backgroundColor: searchResult.eco_tier_color }}
                    >
                      {searchResult.eco_tier}
                    </span>
                    <span className="text-xs font-medium" style={{ color: searchResult.eco_tier_color }}>
                      {searchResult.eco_tier_name?.split(' - ')[0]}
                    </span>
                  </div>
                </div>
              </div>

              {/* Environmental Stats */}
              <div className="grid grid-cols-3 divide-x divide-[#1A1A1A]/5 border-b border-gray-100">
                <div className="p-4 text-center">
                  <Droplets size={16} className="mx-auto text-[#3B7CB8]/60 mb-1" />
                  <p className="text-sm font-medium text-gray-900">
                    {searchResult.water_liters_per_kg ? `${(searchResult.water_liters_per_kg / 1000).toFixed(1)}k L` : 'N/A'}
                  </p>
                  <p className="text-[9px] text-gray-400">Water/kg</p>
                </div>
                <div className="p-4 text-center">
                  <Zap size={16} className="mx-auto text-[#996B3D]/60 mb-1" />
                  <p className="text-sm font-medium text-gray-900">
                    {searchResult.carbon_kg_per_kg ? `${searchResult.carbon_kg_per_kg} kg` : 'N/A'}
                  </p>
                  <p className="text-[9px] text-gray-400">CO₂/kg</p>
                </div>
                <div className="p-4 text-center">
                  <AlertCircle size={16} className={`mx-auto mb-1 ${searchResult.microplastic_shedding ? 'text-[#B85C3B]/60' : 'text-[#1B7340]/60'}`} />
                  <p className="text-sm font-medium text-gray-900">{searchResult.microplastic_shedding ? 'Yes' : 'No'}</p>
                  <p className="text-[9px] text-gray-400">Microplastics</p>
                </div>
              </div>

              {/* Properties */}
              <div className="p-5 space-y-4">
                {/* Vegan & Biodegradable */}
                <div className="flex gap-3">
                  <div className={`flex-1 p-3 rounded-lg ${searchResult.vegan ? 'bg-[#1B7340]/5' : 'bg-[#996B3D]/5'}`}>
                    <div className="flex items-center gap-2">
                      {searchResult.vegan ? (
                        <Check size={14} className="text-[#1B7340]" />
                      ) : (
                        <X size={14} className="text-[#996B3D]" />
                      )}
                      <span className={`text-xs font-medium ${searchResult.vegan ? 'text-[#1B7340]' : 'text-[#996B3D]'}`}>
                        {searchResult.vegan ? 'Vegan' : 'Not Vegan'}
                      </span>
                    </div>
                  </div>
                  <div className={`flex-1 p-3 rounded-lg ${searchResult.biodegradable ? 'bg-[#1B7340]/5' : 'bg-[#B85C3B]/5'}`}>
                    <div className="flex items-center gap-2">
                      {searchResult.biodegradable ? (
                        <Check size={14} className="text-[#1B7340]" />
                      ) : (
                        <X size={14} className="text-[#B85C3B]" />
                      )}
                      <span className={`text-xs font-medium ${searchResult.biodegradable ? 'text-[#1B7340]' : 'text-[#B85C3B]'}`}>
                        {searchResult.biodegradable ? 'Biodegradable' : 'Not Biodegradable'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Best Seasons */}
                {searchResult.best_seasons?.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-gray-400 mb-2">Best Seasons</p>
                    <div className="flex flex-wrap gap-2">
                      {searchResult.best_seasons.map((season, i) => (
                        <span key={i} className="px-3 py-1 bg-gray-50/[0.03] text-gray-500 text-xs rounded-lg">
                          {season}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Certifications */}
                {searchResult.certifications_to_look_for?.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-gray-400 mb-2">Certifications to Look For</p>
                    <div className="flex flex-wrap gap-2">
                      {searchResult.certifications_to_look_for.map((cert, i) => (
                        <span key={i} className="px-3 py-1 bg-[#1B7340]/5 text-[#1B7340] text-xs rounded-lg border border-[#1B7340]/20">
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Price Benchmarks */}
                {searchResult.price_benchmarks && Object.keys(searchResult.price_benchmarks).length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-gray-400 mb-2">Price Range (Apparel)</p>
                    <div className="grid grid-cols-4 gap-2">
                      {['budget', 'mid', 'premium', 'luxury'].map((tier) => (
                        searchResult.price_benchmarks[tier] && (
                          <div key={tier} className="p-2 bg-gray-50/[0.02] rounded-lg text-center">
                            <p className="text-[9px] text-gray-400 capitalize">{tier}</p>
                            <p className="text-xs font-medium text-gray-900">₹{searchResult.price_benchmarks[tier]?.toLocaleString()}</p>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}

                {/* Sustainability Notes */}
                {searchResult.sustainability_notes && (
                  <div className="p-3 bg-white/[0.03] rounded-lg">
                    <p className="text-xs text-gray-500 leading-relaxed">{searchResult.sustainability_notes}</p>
                  </div>
                )}

                {/* Expected Lifespan */}
                {searchResult.expected_lifespan_years && (
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <span className="text-xs text-gray-400">Expected Lifespan</span>
                    <span className="text-sm font-medium text-gray-900">{searchResult.expected_lifespan_years}</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="premium-card rounded-xl p-5 luxury-border text-center">
              <p className="text-sm text-gray-500">{searchResult.message}</p>
              {searchResult.suggestions?.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-gray-400 mb-2">Did you mean:</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {searchResult.suggestions.map((sug, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setSearchQuery(sug);
                          searchFabric();
                        }}
                        className="px-3 py-1 bg-white/5 text-gray-900 text-xs rounded-lg hover:bg-white/10"
                      >
                        {sug.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Browse Fabrics */}
      {!searchResult && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.15em] text-gray-400 mb-3">Browse by Eco-Tier</p>
          
          {loadingList ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="space-y-4">
              {['A', 'B', 'C', 'D', 'E'].map((tier) => {
                const tierFabrics = fabricList.filter(f => f.eco_tier === tier);
                if (tierFabrics.length === 0) return null;
                
                const tierInfo = tierFabrics[0];
                return (
                  <div key={tier}>
                    <div className="flex items-center gap-2 mb-2">
                      <span 
                        className="w-5 h-5 rounded-full flex items-center justify-center text-gray-900 text-[10px] font-medium"
                        style={{ backgroundColor: tierInfo.eco_tier_color }}
                      >
                        {tier}
                      </span>
                      <span className="text-xs text-gray-500">{tierInfo.eco_tier_name?.split(' - ')[0]}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {tierFabrics.map((fabric, i) => (
                        <button
                          key={i}
                          onClick={() => handleFabricClick(fabric.key)}
                          className="px-3 py-1.5 bg-gray-50 border border-[#000000]/20 text-gray-600 
                            text-xs rounded-lg hover:border-[#000000]/30 hover:text-gray-900 transition-all"
                        >
                          {fabric.name}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Overview Section (existing content)
  const renderOverview = () => {
    if (!lastScanned) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
          <div className="w-24 h-24 rounded-3xl flex items-center justify-center mb-5">
            <img 
              src="/logos/logo-black.jpg" 
              alt="ATELIA.AI" 
              className="w-16 h-16 object-contain opacity-40"
            />
          </div>
          <h2 className="text-xl font-bold text-black text-center mb-2 uppercase">
            No Insights Yet
          </h2>
          <p className="text-black/55 text-center text-sm font-normal max-w-[260px] leading-relaxed">
            Scan your first garment to unlock detailed fabric intelligence, sustainability scores, and price analysis.
          </p>
          <button
            onClick={handleStartScan}
            className="mt-6 px-8 py-3 bg-black text-white rounded-2xl text-sm font-bold tracking-wider
              flex items-center gap-3 hover:bg-black/90 transition-all duration-300 uppercase"
          >
            <ScanLine size={16} strokeWidth={2} />
            <span>Scan a Garment</span>
          </button>
        </div>
      );
    }

    const shortcuts = [
      {
        label: 'Fabric Intelligence',
        screen: 'fabric-intelligence',
        icon: Sparkles,
        score: lastScanned.scores?.fabric || lastScanned.fabricIntelligence?.overall_score || 70,
        color: '#000000'
      },
      {
        label: 'Sustainability',
        screen: 'sustainability',
        icon: Leaf,
        score: lastScanned.scores?.sustainability || lastScanned.sustainabilityDetails?.overall_score || 60,
        color: '#1B7340'
      }
    ];

    return (
      <>
        {/* Last Scanned Product */}
        <div className="px-6">
          <p className="text-[10px] text-gray-300 uppercase tracking-[0.2em] mb-3">Last Scanned</p>
          
          <div 
            onClick={() => setCurrentScreen('product-info')}
            className="premium-card rounded-2xl luxury-border overflow-hidden
              cursor-pointer transform hover:scale-[1.01] transition-all duration-500"
          >
            <div className="flex">
              {lastScanned.imageUrl && (
                <img
                  src={lastScanned.imageUrl}
                  alt={lastScanned.name}
                  className="w-28 h-28 object-cover"
                />
              )}
              <div className="p-4 flex-1">
                <p className="text-[10px] text-gray-600 font-medium uppercase tracking-[0.15em]">
                  {lastScanned.brand}
                </p>
                <h3 className="font-sans text-base font-light text-black mt-1">
                  {lastScanned.name}
                </h3>
                {lastScanned.tags && (
                  <div className="flex gap-1 mt-2">
                    {lastScanned.tags.slice(0, 2).map((tag, i) => (
                      <span
                        key={i}
                        className="px-2 py-0.5 bg-gray-50/[0.03] text-gray-500 text-[10px] rounded-md"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Summary Scores */}
        <div className="px-6 mt-6">
          <p className="text-[10px] text-gray-300 uppercase tracking-[0.2em] mb-3">Summary Scores</p>
          
          <div className="grid grid-cols-3 gap-3">
            {shortcuts.map((item, i) => {
              const Icon = item.icon;
              return (
                <div
                  key={i}
                  className="premium-card rounded-xl p-4 luxury-border text-center"
                >
                  <Icon size={16} strokeWidth={1.5} style={{ color: item.color }} className="mx-auto mb-2 opacity-60" />
                  <p className="text-[10px] text-gray-300 mb-1 tracking-wide truncate">{item.label.split(' ')[0]}</p>
                  <p className="font-serif text-xl font-light" style={{ color: item.color }}>
                    {Math.round(item.score)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Access */}
        <div className="px-6 mt-6">
          <p className="text-[10px] text-gray-300 uppercase tracking-[0.2em] mb-3">Quick Access</p>
          
          <div className="space-y-2">
            {shortcuts.map((item, i) => {
              const Icon = item.icon;
              return (
                <button
                  key={i}
                  onClick={() => setCurrentScreen(item.screen)}
                  className="w-full premium-card rounded-xl p-4 luxury-border
                    flex items-center gap-3 hover:shadow-lg hover:shadow-black/[0.02] transition-all duration-500"
                >
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${item.color}08` }}
                  >
                    <Icon size={18} strokeWidth={1.5} style={{ color: item.color }} className="opacity-60" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-gray-900 tracking-wide">{item.label}</p>
                  </div>
                  <ArrowRight size={16} strokeWidth={1.5} className="text-gray-200" />
                </button>
              );
            })}
          </div>
        </div>

        {/* Scan Another */}
        <div className="px-6 mt-6">
          <button
            onClick={handleStartScan}
            className="w-full py-3 border border-dashed border-[#000000]/20 rounded-2xl
              text-gray-500 text-xs font-light tracking-wider flex items-center justify-center gap-2
              hover:border-[#000000]/30 hover:bg-white/[0.02] transition-all duration-500"
          >
            <ScanLine size={16} strokeWidth={1.5} />
            <span>Scan Another Garment</span>
          </button>
        </div>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Header */}
      <div className="px-6 pt-10 pb-4">
        <p className="text-xs uppercase tracking-[0.2em] text-black/55 mb-2">Dashboard</p>
        <h1 className="font-sans text-2xl font-light text-black">
          Insights
        </h1>
        <p className="text-black/55 text-sm font-light mt-1">
          Your garment intelligence hub
        </p>
      </div>

      {/* Section Tabs */}
      <div className="px-6 mb-4">
        <div className="flex gap-1 p-1 bg-gray-50/[0.03] rounded-xl">
          <button
            onClick={() => setActiveSection('overview')}
            className={`flex-1 py-2 px-2 rounded-lg text-[11px] font-medium transition-all ${
              activeSection === 'overview'
                ? 'bg-gray-50 text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-600'
            }`}
            data-testid="insights-tab-overview"
          >
            Overview
          </button>
          <button
            onClick={() => setActiveSection('fabric-lookup')}
            className={`flex-1 py-2 px-2 rounded-lg text-[11px] font-medium transition-all flex items-center justify-center gap-1 ${
              activeSection === 'fabric-lookup'
                ? 'bg-gray-50 text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-600'
            }`}
            data-testid="insights-tab-fabric"
          >
            <Search size={12} />
            Fabrics
          </button>
          <button
            onClick={() => setActiveSection('cert-filter')}
            className={`flex-1 py-2 px-2 rounded-lg text-[11px] font-medium transition-all flex items-center justify-center gap-1 ${
              activeSection === 'cert-filter'
                ? 'bg-gray-50 text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-600'
            }`}
            data-testid="insights-tab-certs"
          >
            <Shield size={12} />
            Certs
          </button>
        </div>
      </div>

      {/* Content */}
      {activeSection === 'overview' && renderOverview()}
      {activeSection === 'fabric-lookup' && renderFabricLookup()}
      {activeSection === 'cert-filter' && renderCertificationFilter()}
    </div>
  );
};

export default InsightsScreen;
